import { isShapeModel } from '@src/slide/domain/ui-model/elements/element-judge';
import { EInsertType } from '@tencent/ai-sdk-interface';
import { IShapeModel } from '@src/slide/domain/ui-model/element-modeles/Interface/shape-model.interface';
import {
    SelectedModelType,
} from '@src/slide/application/service/selection/selection-service.interface';
import { UIModelType } from '@src/slide/domain/ui-model/element-modeles/constant';
import { IAIAssistantService } from '@src/slide/application/service/ai-ssistant/ai-ssistant-service.interface';
import { tdw } from '@src/slide/infrastructure/module/dependent/report/offline-data-report/tdw-report';
import {
    TDWActionType,
    TDWModuleType,
    TDWOperationType,
} from '@src/slide/infrastructure/module/dependent/report/offline-data-report/constant';
import { IAiFloatManagerDelegate } from '@src/slide/application/service/ai-float-manager/ai-manager.interface';
import { browser } from '@utils/agent';
import ua from '@tencent/docs-user-agent';

/* docs-ai 生命周期钩子函数，可在 docs-ai 中进行拓展 */
export interface SlideContractInfer {
    done: () => void;
    insert: (botMessage: string, insertType: EInsertType) => void;
    discard: () => void;
    write: (botMessage: string) => void;
    commit: () => void;
    onModalClose: () => void;
}

interface ISlideContractProps {
    services: IAiFloatManagerDelegate;
    isGeneratingPage?: boolean;
    updateCanvasHeight: (height: number) => void;
}

export enum MODE {
    generatingPage, // 生成页
    aiWithElement, // 选中 element 后进行 AI 写作
}

export class SlideContract implements SlideContractInfer    {
    private selectedModel?: IShapeModel;
    private mode?: MODE;
    private services: IAiFloatManagerDelegate;
    private mountDom?: HTMLDivElement;
    private mobileWritingObserver: ResizeObserver;

    public constructor(props: ISlideContractProps) {
        const {
            services,
            isGeneratingPage,
            updateCanvasHeight,
        } = props;
        this.services = services;
        this.mode = isGeneratingPage ? MODE.generatingPage : MODE.aiWithElement;


        if (ua.isMobile) {
            this.mobileWritingObserver = new ResizeObserver((entries) => {
                entries.forEach((entry) => {
                    // element 为 ai-float-modal
                    const element = entry.target as HTMLDivElement;
                    const height = element.clientHeight;
                    updateCanvasHeight(height);
                });
            });
        }
    }

    public setMode(mode: MODE) {
        this.mode = mode;
    }

    public setMountDom(ele: HTMLDivElement) {
        this.mountDom = ele;
    }
    /**
     * docs-ai 触发插入
     * @param botMessage 插入内容
     * @param insertType 插入类型
     *     INSERT, // 插入（无选区）
     *     REPLACE, // 替换原文（有选区）
     *     INSERT_BELOW, // 插入到下方（有选区）
     */
    public insert(botMessage: string, insertType: EInsertType): void {
        if (this.mode === MODE.generatingPage) {
            /* 生成页插入 */
            this.getAiAssistantService()?.insertSlidePage(botMessage);
        } else if (this.mode === MODE.aiWithElement) {
            /* 元素内容插入 */
            const element = this.getSelectedModel();
            if (element?.type !== UIModelType.SHAPE) return;
            const selectedModel = element as IShapeModel;
            if (!selectedModel?.editorInstance) return;

            if (insertType === EInsertType.REPLACE) {
                if (!selectedModel.editorInstance.isEditing) {
                    selectedModel.editorInstance.setSelectionAll();
                }
            }

            if (insertType === EInsertType.INSERT_BELOW) {
                selectedModel.editorInstance.editor.selectEnd();
            }

            selectedModel.editorInstance.insertTextWithNoAutoSave(
                botMessage,
                undefined,
            );

            const { paragraphList, textBodyStyle } = selectedModel.editorInstance.getData();
            selectedModel.updateParagraphList(paragraphList, textBodyStyle);
        }

        // 上报生成页和AI写作的插入正文
        tdw({
            opername: TDWOperationType.DOC_PPT_AI,
            module: this.mode === MODE.generatingPage ? TDWModuleType.PAGE_CREATE : TDWModuleType.AI_WRITE,
            action: TDWActionType.AI_INSERT,
            ver5: browser.isMobile ? TDWModuleType.MOBILE : TDWModuleType.PC,
        });
    }
    /**
     * docs-ai 正在流式输出内容
     * @param botMessage 目前的输出内容
     */
    public write(botMessage: string): void {
        if (this.selectedModel && isShapeModel(this.selectedModel)) {
            // todo：ShapeModel 更新内容
        }
    }
    /**
     * docs-ai 流式输出结束
     */
    public done(): void {}

    public stop(): void {
        console.log('===stop');
    }

    /**
     * docs-ai 在 Board 上的流式输出正在进行
     */
    public onBoardWrite(botMessage: string): void {
        // 上报生成页和AI写作的正在写作
        console.log('===onBoardWrite');
        if (ua.isMobile) {
            this.mountDom?.children?.[0] && this.mobileWritingObserver.observe(this.mountDom.children[0]);
        }
    }

    /**
     * docs-ai 在 Board 上的流式输出结束
     */
    public onBoardDone(): void {
        console.log('===onBoardDone');
        // 上报生成页和AI写作的 写作（生成大纲）完成
        tdw({
            opername: TDWOperationType.DOC_PPT_AI,
            module: this.mode === MODE.generatingPage ? TDWModuleType.PAGE_CREATE : TDWModuleType.AI_WRITE,
            action: this.mode === MODE.generatingPage ? TDWActionType.AI_OUTLINE_SUCCESS : TDWActionType.AI_SUCCESS,
            ver5: browser.isMobile ? TDWModuleType.MOBILE : TDWModuleType.PC,
        });
    }

    public discard(): void {
        console.log('===discard');
        // 上报丢弃
        tdw({
            opername: TDWOperationType.DOC_PPT_AI,
            module: this.mode === MODE.generatingPage ? TDWModuleType.PAGE_CREATE : TDWModuleType.AI_WRITE,
            action: TDWActionType.AI_DELETE,
            ver5: browser.isMobile ? TDWModuleType.MOBILE : TDWModuleType.PC,
        });
    }

    public commit(): void {}

    public onModalClose(): void {
        this.mobileWritingObserver?.disconnect();
    }

    private getSelectedModel(): SelectedModelType {
        return this.services.selectionService.getSelectedModel();
    }

    private getAiAssistantService(): IAIAssistantService | undefined {
        return this.services.aiAssistantService;
    }
}
